import React from 'react';
import { FiBell, FiInfo, FiClock, FiX, FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const NotificationPanel = ({
    isOpen,
    onClose,
    notifications,
    loading,
    onMarkAsRead,
    onMarkAllRead,
    unreadCount
}) => {
    if (!isOpen) return null;

    return (
        <>
            {/* --- DESKTOP DROPDOWN --- */}
            <div className="hidden lg:block absolute top-[calc(100%+10px)] right-0 w-80 bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.15)] border border-slate-100 overflow-hidden z-[5001] animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-50 bg-slate-50/50">
                    <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        Notifications
                        {unreadCount > 0 && (
                            <span className="bg-rose-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-black">
                                {unreadCount}
                            </span>
                        )}
                    </h3>
                    {unreadCount > 0 && (
                        <button
                            onClick={onMarkAllRead}
                            className="text-[11px] font-bold text-indigo-600 hover:text-indigo-700 transition-colors"
                        >
                            Mark all as read
                        </button>
                    )}
                </div>

                <div className="max-h-[400px] overflow-y-auto hide-scrollbar">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-10">
                            <div className="w-8 h-8 border-2 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-3"></div>
                            <p className="text-xs text-slate-400 font-medium">Loading...</p>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                            <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                                <FiBell className="w-6 h-6 text-slate-200" />
                            </div>
                            <p className="text-sm text-slate-900 font-bold mb-1">No notifications yet</p>
                            <p className="text-xs text-slate-400 font-medium leading-relaxed">We'll notify you when you have updates.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-50">
                            {notifications.map((n) => (
                                <div
                                    key={n._id}
                                    onClick={() => n.status !== 'read' && onMarkAsRead(n._id)}
                                    className={`p-4 hover:bg-slate-50 transition-colors cursor-pointer group relative ${n.status !== 'read' ? 'bg-indigo-50/20' : ''}`}
                                >
                                    {n.status !== 'read' && (
                                        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-indigo-500"></div>
                                    )}
                                    <div className="flex gap-3">
                                        <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${n.status !== 'read' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                            <FiInfo className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between gap-2 mb-0.5">
                                                <h4 className={`text-xs font-bold leading-tight truncate ${n.status !== 'read' ? 'text-indigo-950' : 'text-slate-700'}`}>
                                                    {n.title}
                                                </h4>
                                                <span className="text-[9px] font-bold text-slate-400 whitespace-nowrap">
                                                    {new Date(n.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <p className={`text-[11px] line-clamp-2 leading-relaxed ${n.status !== 'read' ? 'text-indigo-900/70' : 'text-slate-500'}`}>
                                                {n.message}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <Link
                    to="/worker/dashboard?tab=notifications"
                    onClick={onClose}
                    className="block w-full py-3.5 bg-slate-50 hover:bg-slate-100 text-center text-[11px] font-black uppercase tracking-widest text-slate-500 transition-colors border-t border-slate-100"
                >
                    View All Notifications
                </Link>
            </div>

            {/* --- MOBILE SLIDE-OVER --- */}
            <div className="lg:hidden fixed inset-0 z-[6000]">
                {/* Backdrop */}
                <div
                    className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                    onClick={onClose}
                    style={{ animation: 'fadeIn 0.2s ease-out' }}
                />

                {/* Panel */}
                <div
                    className="absolute top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white shadow-[-10px_0_40px_rgba(0,0,0,0.2)]"
                    style={{ animation: 'slideFromRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}
                >
                    <div className="flex flex-col h-full bg-[#FCFCFD]">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 pt-12 pb-6 bg-white border-b border-slate-100">
                            <div>
                                <h3 className="text-xl font-ubuntu font-bold text-slate-900 tracking-tight">Updates</h3>
                                <p className="text-xs text-slate-500 font-medium">Notifications and alerts</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 active:scale-90 transition-transform"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Quick Actions */}
                        <div className="px-6 py-4 flex items-center justify-between bg-white border-b border-slate-100">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                {unreadCount > 0 ? `${unreadCount} Unread Messages` : 'Recent Activity'}
                            </span>
                            {unreadCount > 0 && (
                                <button
                                    onClick={onMarkAllRead}
                                    className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-lg active:scale-95 transition-transform"
                                >
                                    Mark All Read
                                </button>
                            )}
                        </div>

                        {/* Content list */}
                        <div className="flex-1 overflow-y-auto py-2 px-4 hide-scrollbar">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-20">
                                    <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                                    <p className="text-sm text-slate-400 font-medium">Checking for updates...</p>
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 px-10 text-center">
                                    <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mb-6">
                                        <FiBell className="w-10 h-10 text-slate-200" />
                                    </div>
                                    <h4 className="text-slate-900 font-bold mb-2">Clean Slate!</h4>
                                    <p className="text-sm text-slate-400 font-medium leading-relaxed">No new notifications at the moment. We'll let you know when something happens.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {notifications.map((n) => (
                                        <div
                                            key={n._id}
                                            onClick={() => n.status !== 'read' && onMarkAsRead(n._id)}
                                            className={`p-5 rounded-3xl border transition-all active:scale-[0.98] relative overflow-hidden ${n.status !== 'read'
                                                ? 'bg-white border-indigo-100 shadow-[0_10px_25px_rgba(79,70,229,0.08)]'
                                                : 'bg-slate-50/50 border-transparent shadow-none'}`
                                            }
                                        >
                                            {n.status !== 'read' && (
                                                <div className="absolute top-0 right-0 w-8 h-8 flex items-center justify-center">
                                                    <span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(79,70,229,0.6)]"></span>
                                                </div>
                                            )}

                                            <div className="flex gap-4">
                                                <div className={`w-11 h-11 rounded-2xl flex-shrink-0 flex items-center justify-center ${n.status !== 'read' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'bg-white text-slate-300 border border-slate-100'}`}>
                                                    <FiInfo className="w-5.5 h-5.5" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex justify-between items-start mb-1">
                                                        <h4 className={`text-sm font-bold leading-tight truncate pr-4 ${n.status !== 'read' ? 'text-indigo-950' : 'text-slate-600 font-semibold'}`}>
                                                            {n.title}
                                                        </h4>
                                                    </div>
                                                    <p className={`text-xs leading-relaxed mb-3 ${n.status !== 'read' ? 'text-indigo-900/60' : 'text-slate-400'}`}>
                                                        {n.message}
                                                    </p>
                                                    <div className="flex items-center gap-1.5 text-[9px] font-bold text-slate-400 uppercase tracking-widest bg-slate-100/50 inline-flex px-2 py-0.5 rounded-lg">
                                                        <FiClock className="w-2.5 h-2.5" />
                                                        {new Date(n.createdAt).toLocaleDateString()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Sticky Footer */}
                        <div className="p-6 bg-white border-t border-slate-100">
                            <Link
                                to="/worker/dashboard?tab=notifications"
                                onClick={onClose}
                                className="flex items-center justify-center gap-2 w-full py-4 bg-slate-900 text-white rounded-2xl font-bold text-sm active:scale-95 transition-all shadow-xl shadow-slate-900/20"
                            >
                                Go to Inbox
                                <FiArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideFromRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
        </>
    );
};

export default NotificationPanel;
